import OnboardingFooter from '../../components/OnboardingFooter';
import { useFormik } from 'formik';
import { getSignUpSchema } from './schema';
import SignUpUserDetails from '../../views/SignUpUserDetails';
import { useState } from 'react';
import SignUpCompanyDetails from '../../views/SignUpCompanyDetails';
import { CompanySize, IndustryType } from '@EHSNavigator/backend';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { useApiClients } from '../../client/api/useApiClient';
import { SET_LOADING } from '../../reducers/constants';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';

const INITIAL_STATE = {
  fullName: '',
  email: '',
  password: '',
  confirmPassword: '',
  companyName: '',
  industryType: '',
  industrySize: '',
  headquarterAddress: '',
  contact: '',
  dob: '',
  organizationContact: '',
};

export default function SignUp() {
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();
  const { AuthApi } = useApiClients();
  const dispatch = useDispatch<AppDispatch>();
  const signUpHandler = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getSignUpSchema(currentStep),
    onSubmit: async (values) => {
      if (currentStep === 2) {
        try {
          const response = await AuthApi.authControllerRegister({
            createUserDTO: {
              fullName: values.fullName,
              email: values?.email,
              password: values?.password,
              organizationName: values?.companyName,
              industryType: values?.industryType as IndustryType,
              size: values?.industrySize as CompanySize,
              organizationAddress: values?.headquarterAddress,
              organizationContact: values?.organizationContact
                ? String(values?.organizationContact)
                : undefined,
              dob:
                values?.dob && moment(values?.dob).isValid()
                  ? moment(values.dob).format('DD/MM/YYYY')
                  : (undefined as any),
              contact: values?.contact
                ? String(values?.contact)
                : (undefined as any),
            },
          });
          dispatch({
            type: SET_LOADING,
          });
          toast.success(response?.data?.message);
          localStorage.setItem('user', JSON.stringify(response?.data?.user));
          navigate('/pricing-plans');
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          if (Array.isArray(errorMessage)) {
            errorMessage.forEach((msg: string) => toast.error(msg));
          } else {
            toast.error(errorMessage || 'An unexpected error occurred.');
          }
        }
      }
    },
  });
  const handleNextStep = async () => {
    const errors = await signUpHandler.validateForm();
    if (Object.keys(errors).length === 0) {
      const res = await AuthApi.authControllerCheckEmailExist({
        checkEmailExistDTO: {
          email: signUpHandler?.values?.email,
        },
      });
      if (res?.data.exist === false) {
        setCurrentStep(2);
      } else {
        await signUpHandler?.setFieldTouched('email', true);
        await signUpHandler?.setFieldError('email', 'Email already exists');
      }
    } else {
      const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
        acc[key] = true;
        return acc;
      }, {});
      signUpHandler.setTouched(touchedFields);
    }
  };

  return (
    <main className="relative z-10 flex flex-1 flex-col items-center justify-between gap-10 pb-5">
      <div className="flex w-full flex-1 flex-col items-center pt-14 md:pt-16 lg:pt-20">
        <div className="mb-7 flex flex-col items-center gap-2.5 px-10 text-center sm:px-0 md:mb-20 lg:mb-75">
          <img
            src="/assets/images/logo.png"
            alt="logo"
            className="h-85 w-69 md:h-100 md:w-20 lg:h-150 lg:w-121"
          />
          <h2 className="text-brand-byzantine font-bold">Create New Account</h2>
          <h5 className="font-inter-variable medium text-brand-byzantine -tracking-sign-up-header-title text-base leading-5 italic md:text-xl md:leading-6 lg:text-2xl lg:leading-7">
            {currentStep === 1
              ? `Welcome to EHS Navigator, let’s get started!`
              : `Tell us about your company!`}
          </h5>
        </div>
        <div className="w-full px-25">
          <form
            action="#"
            onSubmit={signUpHandler?.handleSubmit}
            className="mx-auto w-full max-w-325 lg:max-w-970"
            aria-labelledby="sign-up-heading"
            noValidate
          >
            {currentStep === 1 && (
              <SignUpUserDetails
                signUpHandler={signUpHandler}
                handleClick={handleNextStep}
              />
            )}
            {currentStep === 2 && (
              <SignUpCompanyDetails signUpHandler={signUpHandler} />
            )}
          </form>
        </div>
      </div>
      <OnboardingFooter />
    </main>
  );
}
